import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerWithFamily } from '../../services/api';
import SecureLS from 'secure-ls';
import { setUser, setFamilies } from '../../utils/secureStorage';
import { useFamily } from '../../contexts/FamilyContext';

function FamilySetup() {
  const navigate = useNavigate();
  const { updateFamilies } = useFamily();
  const [formData, setFormData] = useState({
    familyName: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.familyName.trim()) {
      newErrors.familyName = 'Family name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      try {
        // Get registration data from localStorage
        const registrationData = JSON.parse(sessionStorage.getItem('registrationData') || '{}');
        
        // Prepare the API payload - family creator is always admin
        const apiPayload = {
          email: registrationData.email || '',
          phone: registrationData.phone || '',
          password: registrationData.password || '',
          password2: registrationData.password || '', // Assuming same password for confirmation
          first_name: registrationData.first_name || '',
          last_name: registrationData.last_name || '',
          date_of_birth: registrationData.date_of_birth || '',
          gender: registrationData.gender || 'unknown',
          family_name: formData.familyName,
          family_role: 'admin' // Family creator is always admin
        };

        // Call the API
        const response = await registerWithFamily(apiPayload);
        
        // Store the response data (including person_id for family tree operations)
        setUser({
          id: response.user?.id || response.user_id || response.id,
          person_id: response.user?.person_id || response.person_id,
          email: response.user?.email || response.email,
          first_name: response.user?.first_name || response.first_name,
          last_name: response.user?.last_name || response.last_name,
          family_name: response.family?.name || response.family_name,
          family_role: response.family_role
        });

        // Store the family ID for invitations
        if (response.family?.id) {
          localStorage.setItem('familyId', response.family.id);

          // Store families data for FamilySwitcher and FamilyContext
          const familiesData = [{
            id: response.family.id,
            name: response.family.name,
            role: 'admin',
            member_count: 1,
          }];
          setFamilies(familiesData);
          localStorage.setItem('primaryFamilyId', response.family.id);
          updateFamilies(familiesData, response.family.id);
        } else {
        }

        // Store auth token if provided (following same logic as Login component)
        if (response.token) {
          const ls = new SecureLS({ encodingType: 'aes' });
          ls.set('authToken', response.token);
          localStorage.setItem('isLoggedIn', 'true');
        } else {
        }

        // Store user information in localStorage (following same logic as Login component)
        if (response.user) {
          setUser({
            id: response.user.id,
            person_id: response.user.person_id,
            email: response.user.email,
            first_name: response.user.first_name,
            last_name: response.user.last_name,
          });
        } else {
        }

        // Store family setup data for the invitation flow
        const familyData = {
          ...registrationData,
          familyName: formData.familyName,
          role: 'admin', // Family creator is always admin
          apiResponse: response
        };
        sessionStorage.setItem('familySetupData', JSON.stringify(familyData));
        
        // Navigate to invitation screen
        navigate('/auth/invite-family');
      } catch (error) {
        console.error('Registration error:', error);
        setErrors({ submit: error.message || 'An error occurred during registration' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-300">
          {/* Header */}
          <div className="bg-[#556B2F] text-white p-6 text-center">
            <h1 className="text-2xl font-bold mb-2">Create Your Family</h1>
            <p className="text-base opacity-90">Start your family's story collection</p>
          </div>
          
          {/* Content */}
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="familyName" className="block text-base font-bold text-gray-700 mb-2">
                  Family Name
                </label>
                <input
                  type="text"
                  id="familyName"
                  name="familyName"
                  placeholder="The Johnson Family"
                  value={formData.familyName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-4 text-lg border-2 rounded-lg transition-colors ${
                    errors.familyName ? 'border-red-500' : 'border-gray-300 focus:border-[#556B2F]'
                  } focus:outline-none`}
                />
                {errors.familyName && <p className="text-red-500 text-sm mt-1">{errors.familyName}</p>}
              </div>

              {errors.submit && <p className="text-red-500 text-sm text-center mb-4">{errors.submit}</p>}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#556B2F] text-white border-none py-4 px-4 text-lg font-bold rounded-lg cursor-pointer transition-colors hover:bg-[#4A5D23] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating Family...' : 'Create Family'}
              </button>
            </form>
            
            <p className="text-sm text-center text-gray-600 mt-4">
              You can add more family members next
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FamilySetup; 